import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import SuccessModal from './SuccessModal';
import '../styles/Booking.css';

// Custom Modal for Date & Time Picker
const CustomDatePickerModal = ({ isOpen, onClose, selected, onChange, minDate, label }) => {
  const modalRef = useRef();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div ref={modalRef} style={{
          background: '#1e1e1e',
          border: '1px solid #464646',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 15px 50px rgba(0,0,0,0.8)',
          maxWidth: '440px',
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '15px',
            paddingBottom: '10px',
            borderBottom: '1px solid #464646'
          }}>
            <h3 style={{ margin: 0, color: '#fff', fontSize: '20px', fontWeight: '600' }}>{label}</h3>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#ff6666',
                fontSize: '30px',
                cursor: 'pointer',
                padding: '0 5px',
                lineHeight: '1'
              }}
            >
              ×
            </button>
          </div>
          <div className="custom-datepicker">
            <DatePicker
              selected={selected}
              onChange={onChange}
              minDate={minDate}
              showTimeSelect
              timeIntervals={30}
              dateFormat="d MMM yy h:mm aa"
              inline
            />
          </div>
        </div>
      </div>
    </>
  );
};

const Booking = () => {
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);

  // Guest search & selection
  const [guestSearch, setGuestSearch] = useState('');
  const [guestSuggestions, setGuestSuggestions] = useState([]);
  const [showGuestDropdown, setShowGuestDropdown] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);     // selected existing guest object
  const [isNewGuest, setIsNewGuest] = useState(false);

  // Guest details (used for both existing and new)
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestEmail, setGuestEmail] = useState('');

  const [notes, setNotes] = useState('');
  const [roomTypeId, setRoomTypeId] = useState('');
  const [ratePlanId, setRatePlanId] = useState('');
  const [availableRooms, setAvailableRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [amenities, setAmenities] = useState([]);

  const [selectedRoomIds, setSelectedRoomIds] = useState([]);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [nightlyRate, setNightlyRate] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  useEffect(() => {
    fetchRoomTypes();
    fetchRatePlans();
    fetchAmenities();
  }, []);

  // Guest search (debounced)
  useEffect(() => {
    if (guestSearch.trim().length < 2) {
      setGuestSuggestions([]);
      setShowGuestDropdown(false);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        const term = guestSearch.toLowerCase().trim();
        const q = query(
          collection(db, "guests"),
          where("active", "==", true)
        );
        const snapshot = await getDocs(q);

        const matches = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(g =>
            (g.fullName?.toLowerCase() || '').includes(term) ||
            (g.phone?.toLowerCase() || '').includes(term) ||
            (g.email?.toLowerCase() || '').includes(term)
          )
          .slice(0, 10);   // limit number of suggestions

        setGuestSuggestions(matches);
        setShowGuestDropdown(matches.length > 0 || term.length > 0);
      } catch (err) {
        console.error("Error searching guests:", err);
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [guestSearch]);

  const selectGuest = (guest) => {
    setSelectedGuest(guest);
    setGuestName(guest.fullName || '');
    setGuestPhone(guest.phone || '');
    setGuestEmail(guest.email || '');
    setGuestSearch(`${guest.fullName} • ${guest.phone || guest.email}`);
    setShowGuestDropdown(false);
    setIsNewGuest(false);
  };

  const startNewGuest = () => {
    setSelectedGuest(null);
    setIsNewGuest(true);
    setGuestName('');
    setGuestPhone('');
    setGuestEmail('');
    setGuestSearch('');
    setShowGuestDropdown(false);
  };

  const fetchRoomTypes = async () => {
    try {
      const snapshot = await getDocs(collection(db, "roomTypes"));
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRoomTypes(list.filter(rt => rt.active !== false));
    } catch (err) {
      console.error("Error fetching room types:", err);
    }
  };

  const fetchRatePlans = async () => {
    try {
      const snapshot = await getDocs(collection(db, "ratePlans"));
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRatePlans(list.filter(rp => rp.active !== false));
    } catch (err) {
      console.error("Error fetching rate plans:", err);
    }
  };

  const fetchAmenities = async () => {
    try {
      const snapshot = await getDocs(collection(db, "amenities"));
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAmenities(list.filter(a => a.active !== false));
    } catch (err) {
      console.error("Error fetching amenities:", err);
    }
  };

  // Calculate nightly rate
  // useEffect(() => {
  //   if (!roomTypeId || !ratePlanId) {
  //     setNightlyRate(0);
  //     return;
  //   }

  //   const roomType = roomTypes.find(rt => rt.id === roomTypeId);
  //   const ratePlan = ratePlans.find(rp => rp.id === ratePlanId);

  //   if (!roomType?.baseRate) {
  //     setNightlyRate(0);
  //     return;
  //   }

  //   let rate = roomType.baseRate;

  //   if (ratePlan?.rateAdjustment) {
  //     rate = rate * (1 + ratePlan.rateAdjustment / 100);
  //   }

  //   const includedIds = roomType?.amenityIds || [];
  //   selectedAmenities.forEach(id => {
  //     if (!includedIds.includes(id)) {
  //       const amenity = amenities.find(a => a.id === id);
  //       if (amenity?.price) rate += amenity.price;
  //     }
  //   });

  //   setNightlyRate(Math.round(rate));
  // }, [roomTypeId, ratePlanId, selectedAmenities, roomTypes, ratePlans, amenities]);

  // In Booking.jsx – when calculating nightlyRate (for display & saving)
  useEffect(() => {
    if (!roomTypeId || !ratePlanId) {
      setNightlyRate(0);
      return;
    }

    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    let rate = roomType?.baseRate || 0;

    const ratePlan = ratePlans.find(rp => rp.id === ratePlanId);
    if (ratePlan?.rateAdjustment) {
      rate *= (1 + ratePlan.rateAdjustment / 100);
    }

    // ← Do NOT add amenities here anymore
    setNightlyRate(Math.round(rate));
  }, [roomTypeId, ratePlanId, roomTypes, ratePlans]);

  const calculateTotal = () => {
    if (!checkIn || !checkOut || nightlyRate === 0 || selectedRoomIds.length === 0) return 0;
    
    const nights = Math.ceil((checkOut - checkIn) / 86400000);
    const baseTotal = nightlyRate * nights * selectedRoomIds.length;

    let amenitiesTotal = 0;
    const nightsNum = nights;
    
    selectedAmenities.forEach(id => {
      const amenity = amenities.find(a => a.id === id);
      if (amenity?.price) {
        amenitiesTotal += amenity.price * nightsNum * selectedRoomIds.length; // per room
      }
    });

    return Math.round(baseTotal + amenitiesTotal);
  };

  // Use in useEffect:
  useEffect(() => {
    setTotalPrice(calculateTotal());
  }, [checkIn, checkOut, selectedRoomIds, nightlyRate, selectedAmenities, amenities]);

  // Calculate total price
  useEffect(() => {
    if (!checkIn || !checkOut || checkOut <= checkIn || selectedRoomIds.length === 0 || nightlyRate === 0) {
      setTotalPrice(0);
      return;
    }
    const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    const total = selectedRoomIds.length * nightlyRate * nights;
    setTotalPrice(Math.round(total));
  }, [checkIn, checkOut, selectedRoomIds, nightlyRate]);

  const formatDisplayDate = (date) => {
    if (!date) return "Select date & time";
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
    .replace(',', '')
    .toUpperCase()
    .replace(/(\d{2}):(\d{2})\s([AP]M)/, '$1:$2 $3');
  };

  const handleSearch = async () => {
    if (!checkIn || !checkOut || !roomTypeId) {
      setError("Please select check-in, check-out, and room type");
      setShowModal(true);
      return;
    }
    if (checkOut <= checkIn) {
      setError("Check-out must be after check-in");
      setShowModal(true);
      return;
    }

    setLoading(true);
    setAvailableRooms([]);
    setSelectedRoomIds([]);
    setSearchPerformed(true);
    setSelectedAmenities([]);

    try {
      const roomsQuery = query(
        collection(db, "rooms"),
        where("roomTypeId", "==", roomTypeId),
        where("active", "==", true)
      );
      const roomsSnapshot = await getDocs(roomsQuery);
      const roomList = roomsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      if (roomList.length === 0) {
        setError("No active rooms found for this type");
        setShowModal(true);
        setLoading(false);
        return;
      }

      const searchCheckIn = Timestamp.fromDate(checkIn);
      const searchCheckOut = Timestamp.fromDate(checkOut);
      const available = [];

      for (const room of roomList) {
        if (room.status === 'Out-of-Order') continue;

        const bookingsQuery = query(collection(db, "bookings"), where("roomId", "==", room.id));
        const bookingsSnapshot = await getDocs(bookingsQuery);

        let hasOverlap = false;
        for (const doc of bookingsSnapshot.docs) {
          const b = doc.data();
          if (b.status === 'cancelled' || b.status === 'checked-out') continue;
          if (searchCheckIn.toMillis() < b.checkOut.toMillis() && searchCheckOut.toMillis() > b.checkIn.toMillis()) {
            hasOverlap = true;
            break;
          }
        }
        if (!hasOverlap) available.push(room);
      }

      setAvailableRooms(available);
      if (available.length === 0) {
        setError("No rooms available for selected dates");
        setShowModal(true);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to check availability");
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleRoomSelection = (roomId) => {
    setSelectedRoomIds(prev =>
      prev.includes(roomId)
        ? prev.filter(id => id !== roomId)
        : [...prev, roomId]
    );
  };

  const handleBook = async () => {
    if (selectedRoomIds.length === 0) {
      setError("Please select at least one room");
      setShowModal(true);
      return;
    }

    if (!selectedGuest && !isNewGuest) {
      setError("Please select an existing guest or enter details for a new guest");
      setShowModal(true);
      return;
    }

    if (isNewGuest && (!guestName.trim() || !guestPhone.trim() || !guestEmail.trim())) {
      setError("Please complete all guest details for new guest");
      setShowModal(true);
      return;
    }

    if (totalPrice <= 0) {
      setError("Invalid total price");
      setShowModal(true);
      return;
    }

    setLoading(true);

    try {
      let guestId = null;
      let finalName = guestName.trim();
      let finalPhone = guestPhone.trim();
      let finalEmail = guestEmail.trim().toLowerCase();

      if (isNewGuest) {
        // Create new guest
        const guestRef = await addDoc(collection(db, "guests"), {
          fullName: finalName,
          phone: finalPhone,
          email: finalEmail,
          active: true,
          createdAt: Timestamp.now()
        });
        guestId = guestRef.id;
      } else {
        guestId = selectedGuest.id;
      }

      const nights = Math.ceil((checkOut - checkIn) / 86400000);
      const searchCheckIn = Timestamp.fromDate(checkIn);
      const searchCheckOut = Timestamp.fromDate(checkOut);

      const bookingPromises = selectedRoomIds.map(roomId =>
        addDoc(collection(db, "bookings"), {
          guestId,                        // reference to guests collection
          guestName: finalName,
          guestPhone: finalPhone,
          guestEmail: finalEmail,
          notes: notes.trim(),
          checkIn: searchCheckIn,
          checkOut: searchCheckOut,
          roomId,
          roomTypeId,
          ratePlanId,
          selectedAmenities,
          adults: 1,
          children: 0,
          // totalPrice: nightlyRate * nights,
          // nightlyRate,
          // nights,
          // deposit: 0,
          totalPrice: calculateTotal(),
          nightlyRate,               // base rate only (room + rate plan)
          nights,
          balance: calculateTotal(), // since deposit = 0 at creation
          // balance: nightlyRate * nights,
          status: 'pending',
          paymentStatus: 'pending',
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        })
      );

      await Promise.all(bookingPromises);

      setSuccessMessage(`Successfully booked ${selectedRoomIds.length} room${selectedRoomIds.length > 1 ? 's' : ''}!`);
      setShowModal(true);

      // Reset form
      setCheckIn(null);
      setCheckOut(null);
      setGuestSearch('');
      setSelectedGuest(null);
      setIsNewGuest(false);
      setGuestName('');
      setGuestPhone('');
      setGuestEmail('');
      setNotes('');
      setRoomTypeId('');
      setRatePlanId('');
      setSelectedRoomIds([]);
      setSelectedAmenities([]);
      setAvailableRooms([]);
      setSearchPerformed(false);
    } catch (err) {
      console.error(err);
      setError("Failed to create booking(s)");
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const getExtraAmenities = () => {
    if (!roomTypeId) return [];
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    const includedIds = roomType?.amenityIds || [];
    return amenities.filter(a => !includedIds.includes(a.id));
  };

  return (
    <div className="dashboard-container padding-bottom-d">
      <div className="pos-dashboard booking-dashboard" style={{ width: '100%', margin: 'auto' }}>
        <div>
          <p className="size5 bold caps" style={{ marginTop: '-5px' }}>Book Your Stay</p>

          <div className="row gap-15">
            <div className="column" style={{flexBasis: '50%'}}>
              <label className="input-label">Check-in Date & Time</label>
              <div
                onClick={() => setShowCheckInModal(true)}
                className="input-field"
                style={{
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: checkIn ? '#f8f8f8' : '#888',
                }}
              >
                {formatDisplayDate(checkIn)}
              </div>
            </div>

            <div className="column" style={{flexBasis: '50%'}}>
              <label className="input-label">Check-out Date & Time</label>
              <div
                onClick={() => setShowCheckOutModal(true)}
                className="input-field"
                style={{
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: checkOut ? '#f8f8f8' : '#888',
                }}
              >
                {formatDisplayDate(checkOut)}
              </div>
            </div>
          </div>

          <CustomDatePickerModal
            isOpen={showCheckInModal}
            onClose={() => setShowCheckInModal(false)}
            selected={checkIn}
            onChange={setCheckIn}
            minDate={new Date()}
            label="Select Check-in Date & Time"
          />

          <CustomDatePickerModal
            isOpen={showCheckOutModal}
            onClose={() => setShowCheckOutModal(false)}
            selected={checkOut}
            onChange={setCheckOut}
            minDate={checkIn || new Date()}
            label="Select Check-out Date & Time"
          />

          <div className="row align-center space-between gap-15" style={{ marginTop: '-10px'}}>
            <div className="column" style={{flexBasis: '50%'}}>
              <label className="input-label">Room Type</label>
              <select
                value={roomTypeId}
                onChange={(e) => {
                  setRoomTypeId(e.target.value);
                  setAvailableRooms([]);
                  setSelectedRoomIds([]);
                  setSelectedAmenities([]);
                  setSearchPerformed(false);
                }}
                className="input-field"
              >
                <option value="">Select Room Type</option>
                {roomTypes.map(rt => (
                  <option key={rt.id} value={rt.id}>
                    {rt.name} - ₦{rt.baseRate?.toLocaleString() || '0'}/night
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handleSearch}
              disabled={loading}
              className="list-btn check-availability-button"
              style={{ marginTop: '-20px', flexBasis: '50%', border: '1px solid #464646' }}
            >
              {loading ? 'Checking...' : 'Check Availability'}
            </button>
          </div>
        </div>

        <div className="scroll-fixed6 booking-form" style={{ marginTop: '-5px', padding: '10px', border: '1px solid #464646', borderRadius: '8px' }}>
          {!searchPerformed ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
              <p className="size5 bold caps">Ready to Book?</p>
              <p>Select dates and room type, then check availability</p>
            </div>
          ) : availableRooms.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#f66' }}>
              <p className="size5 bold caps">No Rooms Available</p>
              <p>Try different dates or room type</p>
            </div>
          ) : (
            <>
              <div className="booking-form">
                <p className="size5 bold caps">Select Rooms ({selectedRoomIds.length} selected)</p>

                <div className="column">
                  <label className="input-label">Available Rooms</label>
                  <div className="extra-amenities">
                    {availableRooms.map(room => (
                      <label key={room.id} style={{ display: 'block', marginBottom: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          className="gold-tick"
                          checked={selectedRoomIds.includes(room.id)}
                          onChange={() => toggleRoomSelection(room.id)}
                          style={{ marginRight: '8px' }}
                        />
                        <strong>Room {room.roomNumber}</strong>
                        {room.floor && <span> - Floor {room.floor}</span>}
                        {room.features && <span> ({room.features})</span>}
                        <span style={{ marginLeft: '12px', color: '#aaa', fontSize: '14px' }}>
                          ₦{nightlyRate.toLocaleString()}/night
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="desktop-form-input">
                  <div className="column">
                    <label className="input-label">Rate Plan</label>
                    <select value={ratePlanId} onChange={(e) => setRatePlanId(e.target.value)} className="input-field">
                      <option value="">Select Rate Plan</option>
                      {ratePlans.map(rp => (
                        <option key={rp.id} value={rp.id}>
                          {rp.name} {rp.rateAdjustment ? `(${rp.rateAdjustment > 0 ? '+' : ''}${rp.rateAdjustment}%)` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {getExtraAmenities().length > 0 && (
                  <div className="column">
                    <label className="input-label">Add Extra Amenities (applies to all selected rooms)</label>
                    <div className="extra-amenities">
                      {getExtraAmenities().map(amenity => (
                        <label key={amenity.id} style={{ display: 'block', marginBottom: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            className="gold-tick"
                            checked={selectedAmenities.includes(amenity.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedAmenities([...selectedAmenities, amenity.id]);
                              } else {
                                setSelectedAmenities(selectedAmenities.filter(id => id !== amenity.id));
                              }
                            }}
                            style={{ marginRight: '8px' }}
                          />
                          <strong>{amenity.name}</strong> (+₦{amenity.price?.toLocaleString() || '0'}/night)
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Guest Selection / Creation */}
                <div className="form-group column" style={{ position: 'relative' }}>
                  <label className="input-label">Guest *</label>
                  <input
                    type="text"
                    value={guestSearch}
                    onChange={(e) => {
                      setGuestSearch(e.target.value);
                      if (!isNewGuest) setSelectedGuest(null);
                    }}
                    onFocus={() => setShowGuestDropdown(true)}
                    placeholder="Search by name, phone or email..."
                    className="input-field"
                  />

                  {showGuestDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: '#333',
                      border: '1px solid #464646',
                      borderRadius: '6px',
                      maxHeight: '220px',
                      overflowY: 'auto',
                      zIndex: 10,
                      marginTop: '2px'
                    }}>
                      {guestSuggestions.map(guest => (
                        <div
                          key={guest.id}
                          onClick={() => selectGuest(guest)}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #464646',
                            fontSize: '14px'
                          }}
                        >
                          <strong>{guest.fullName}</strong><br />
                          <span style={{ color: '#aaa', fontSize: '12px' }}>
                            {guest.phone} • {guest.email}
                          </span>
                        </div>
                      ))}

                      <div
                        onClick={startNewGuest}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          background: '#2a2a2a',
                          borderTop: '1px solid #464646',
                          fontWeight: 'bold',
                          textAlign: 'center'
                        }}
                      >
                        + Register New Guest
                      </div>
                    </div>
                  )}
                </div>

                {(selectedGuest || isNewGuest) && (
                  <div style={{
                    margin: '12px 0',
                    padding: '12px',
                    background: '#1a1a1a',
                    borderRadius: '8px',
                    border: '1px solid #464646'
                  }}>
                    <strong>Guest Details</strong>
                    {isNewGuest ? (
                      <div className="column" style={{ marginTop: '10px' }}>
                        <input
                          type="text"
                          value={guestName}
                          onChange={e => setGuestName(e.target.value)}
                          placeholder="Full name *"
                          className="input-field"
                          style={{ marginBottom: '8px' }}
                        />
                        <input
                          type="tel"
                          value={guestPhone}
                          onChange={e => setGuestPhone(e.target.value)}
                          placeholder="Phone number *"
                          className="input-field"
                          style={{ marginBottom: '8px' }}
                        />
                        <input
                          type="email"
                          value={guestEmail}
                          onChange={e => setGuestEmail(e.target.value)}
                          placeholder="Email address *"
                          className="input-field"
                        />
                      </div>
                    ) : (
                      <div style={{ marginTop: '8px', fontSize: '14px' }}>
                        <strong>{selectedGuest.fullName}</strong><br />
                        Phone: {selectedGuest.phone}<br />
                        Email: {selectedGuest.email}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group column">
                  <label className="input-label">Notes (Optional)</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Special requests, preferences, etc..."
                    className="input-field"
                    rows="3"
                    style={{ resize: 'vertical', minHeight: '80px' }}
                  />
                </div>

                <div style={{ marginBottom: '15px', padding: '12px', background: '#131313ff', borderRadius: '8px' }}>
                  <strong>Nightly Rate (per room): ₦{nightlyRate.toLocaleString()}</strong><br />
                  <strong>Total ({selectedRoomIds.length} room{selectedRoomIds.length !== 1 ? 's' : ''}): ₦{totalPrice.toLocaleString()}</strong>
                  {totalPrice > 0 && (
                    <div style={{ fontSize: '14px', marginTop: '6px' }}>
                      {Math.ceil((checkOut - checkIn) / 86400000)} night(s)
                    </div>
                  )}
                </div>

                <button
                  onClick={handleBook}
                  disabled={loading || selectedRoomIds.length === 0 || (!selectedGuest && !isNewGuest)}
                  className="login-button"
                  style={{marginBottom: '30px'}}
                >
                  {loading ? 'Processing...' : `Book ${selectedRoomIds.length} Room${selectedRoomIds.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </>
          )}
        </div>

        {showModal && (
          <SuccessModal
            icon={error ? "xmark" : "check"}
            iconColor={error ? "red" : "green"}
            message={error || successMessage}
            onClose={() => {
              setError("");
              setSuccessMessage("");
              setShowModal(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Booking;